import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import type { RuntimeQueues, RuntimeService, RuntimeWorker } from '@/types';
import { displayStatusLabel, isServiceReachable } from '@/utils/runtimeService';

type Props = {
  service: RuntimeService;
  workers?: RuntimeWorker[];
  queues?: RuntimeQueues | null;
  onClose: () => void;
};

function MetricRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="runtime-detail-row">
      <span className="runtime-detail-label">{label}</span>
      <span className="runtime-detail-value">{value}</span>
    </div>
  );
}

function CopyableCommand({ command, label }: { command: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="runtime-cmd-block">
      <p className="modal-hint">{label}</p>
      <div className="runtime-cmd-row">
        <pre className="runtime-start-cmd">{command}</pre>
        <button type="button" className="btn btn-sm runtime-copy-btn" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function shortWorkerId(workerId: string): string {
  const parts = workerId.split(':');
  if (parts.length >= 2) {
    return `${parts[0]}:${parts[1]}`;
  }
  return workerId.length > 28 ? `${workerId.slice(0, 28)}…` : workerId;
}

function workersForService(serviceId: string, workers: RuntimeWorker[]): RuntimeWorker[] {
  if (serviceId === 'scrape-workers' || serviceId === 'scrape-worker') {
    return workers.filter((w) => w.role === 'scrape-worker');
  }
  if (serviceId === 'send-workers' || serviceId === 'send-worker') {
    return workers.filter((w) => w.role === 'send-worker');
  }
  if (serviceId === 'scheduler' || serviceId === 'collector') {
    return workers.filter((w) => w.role === 'scheduler');
  }
  if (serviceId === 'civic-alert') {
    return workers.filter((w) => w.role === 'daemon');
  }
  if (serviceId === 'api') {
    return workers.filter((w) => w.role === 'api');
  }
  return [];
}

function WorkersPanel({ workers }: { workers: RuntimeWorker[] }) {
  if (workers.length === 0) {
    return <p className="modal-hint">No active worker heartbeats reported.</p>;
  }
  return (
    <div className="runtime-workers-table-wrap">
      <table className="runtime-workers-table">
        <thead>
          <tr>
            <th>Role</th>
            <th>Status</th>
            <th>Worker</th>
            <th>Current job</th>
            <th>Last seen</th>
            <th>Done</th>
            <th>Err</th>
          </tr>
        </thead>
        <tbody>
          {workers.map((w) => (
            <tr key={w.workerId} className={w.stale ? 'runtime-worker-stale' : undefined}>
              <td>{w.role}</td>
              <td>
                {w.status}
                {w.stale ? ' (stale)' : ''}
              </td>
              <td title={w.workerId}>{shortWorkerId(w.workerId)}</td>
              <td>{w.currentJob || '—'}</td>
              <td>{new Date(w.lastSeenAt).toLocaleString()}</td>
              <td>{w.processedCount}</td>
              <td>{w.errorCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OpenLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="btn btn-sm runtime-detail-open"
    >
      {label}
    </a>
  );
}

export default function ServiceDetailModal({ service, workers = [], queues, onClose }: Props) {
  const m = service.metrics || {};
  const d = service.deployment;
  const reachable = isServiceReachable(service);
  const openUrl = reachable ? service.public_url || service.url || service.admin_url : null;
  const roleWorkers = workersForService(service.id, workers);
  const isScrape = service.id === 'scrape-workers' || service.id === 'scrape-worker';
  const isSend = service.id === 'send-workers' || service.id === 'send-worker';
  const h = service.health;

  return createPortal(
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal card card-elevated runtime-detail-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="runtime-detail-title"
      >
        <h2 id="runtime-detail-title">{service.label}</h2>
        <p className="modal-lead">
          Status: <strong>{displayStatusLabel(service)}</strong>
          {service.kind ? ` · ${service.kind}` : ''}
        </p>
        {service.detail && service.detail !== 'unavailable' && service.status !== 'optional' && (
          <p className="modal-hint">{service.detail}</p>
        )}
        {service.error && <p className="runtime-error">{service.error}</p>}

        {h && (
          <div className="runtime-detail-grid runtime-health-grid">
            <MetricRow label="Running" value={h.running} />
            <MetricRow label="Healthy" value={h.healthy} />
            <MetricRow label="Reachable" value={h.reachable} />
          </div>
        )}

        {d && (
          <section className="runtime-workers-section">
            <h3 className="runtime-workers-title">Deployment</h3>
            <div className="runtime-detail-grid">
              <MetricRow label="Image" value={d.image_name && d.image_tag ? `${d.image_name}:${d.image_tag}` : d.image_name} />
              <MetricRow label="Image ID" value={d.image_id} />
              <MetricRow label="Git commit" value={d.git_commit_short} />
              <MetricRow label="Build date" value={d.build_date} />
              <MetricRow label="Environment" value={d.environment} />
              <MetricRow label="Container status" value={d.container_status} />
              <MetricRow
                label="Started"
                value={d.started_at ? new Date(d.started_at).toLocaleString() : null}
              />
            </div>
          </section>
        )}

        {service.status === 'optional' && service.start_hint && (
          <CopyableCommand command={service.start_hint} label="Start this admin tool:" />
        )}

        <div className="runtime-detail-grid">
          {service.id === 'postgres' && (
            <>
              <MetricRow label="Database" value={m.database as string} />
              <MetricRow label="Connections" value={m.connections as number} />
              <MetricRow label="Tables" value={m.tables as number} />
              <MetricRow label="Subscriptions" value={m.subscriptions as number} />
              <MetricRow label="Discovered links" value={m.discovered_links as number} />
              <MetricRow label="Deliveries" value={m.deliveries as number} />
            </>
          )}
          {service.id === 'redis' && (
            <>
              <MetricRow label="Memory" value={m.used_memory_human as string} />
              <MetricRow label="Scrape queue" value={queues?.scrapeJobs ?? m.scrape_queue_depth as number} />
              <MetricRow label="Send queue" value={queues?.sendJobs ?? m.send_queue_depth as number} />
            </>
          )}
          {isScrape && (
            <>
              <MetricRow label="Scrape queue depth" value={queues?.scrapeJobs ?? m.queue_depth as number} />
              <MetricRow label="Active workers" value={m.active_workers as number} />
              <MetricRow label="Busy" value={m.busy_workers as number} />
              <MetricRow label="Idle" value={m.idle_workers as number} />
              <MetricRow label="Stale" value={m.stale_workers as number} />
              <MetricRow label="Mode" value={m.mode as string} />
            </>
          )}
          {isSend && (
            <>
              <MetricRow label="Active workers" value={m.active_workers as number} />
              <MetricRow label="Busy" value={m.busy_workers as number} />
              <MetricRow label="Idle" value={m.idle_workers as number} />
              <MetricRow label="Stale" value={m.stale_workers as number} />
              <MetricRow label="Mode" value={m.mode as string} />
              <MetricRow label="Redis send queue" value={queues?.sendJobs ?? m.send_queue_depth as number} />
              <MetricRow label="Note" value={m.note as string} />
            </>
          )}
        </div>

        {(isScrape || isSend || roleWorkers.length > 0) && (
          <section className="runtime-workers-section">
            <h3 className="runtime-workers-title">Workers</h3>
            <WorkersPanel workers={roleWorkers} />
          </section>
        )}

        <div className="runtime-detail-actions">
          {openUrl && <OpenLink href={openUrl} label={`Open ${service.label}`} />}
        </div>

        {service.restart_hint && (
          <CopyableCommand command={service.restart_hint} label="Restart stack:" />
        )}

        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
